import React, { FC, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { ELocalStorageKeys } from '@constants/localStorageKeys';

import { Alert } from '@components/common/Alert';
import { Button } from '@components/common/Button';
import { Column, Grid } from '@components/common/Grid';
import { Portal } from '@components/Portal';
import { useToaster } from '@components/stores/toaster';
import { CardHeader } from '@components/StudioApp/agents/main/components/CardHeader';
import { SurveyBuilderSpriteSVG } from '@components/svgs';
import { AddToInsightSVG } from '@components/svgs';
import { track } from '@components/tracking';
import { useFeature } from '@hooks/useFeature';
import { useLocalStorage } from '@hooks/useLocalStorage';

import { Blocks } from './components/Blocks';
import { BlockSettings } from './components/BlockSettings';
import { SurveyBuilderSkeleton } from './components/SurveyBuilderSkeleton';
import { surveyBuilderApi as api } from './api';
import { layoutToggleSuccess } from './toasts';
import { Models } from '.';

export interface Props {
  surveyId: number;
  setActiveBlock?: (block?: Models.Block) => void;
  isStudioVersion?: boolean;
}

export const SurveyBuilder: FC<Props> = ({ surveyId, setActiveBlock, isStudioVersion = false }) => {
  const [activeId, setActiveId] = useState<Models.Block['id']>();

  const { data: initialBlocks = [], isLoading } = api.useGetSurveyBuilderBlocksQuery(surveyId);
  const [createBlock] = api.useCreateSurveyBuilderBlockMutation();
  const [createBlockable] = api.useCreateSurveyBuilderBlockableMutation();

  const [updateStudy] = api.useUpdateStudyMutation();

  const [layoutAlertHidden, setLayoutAlertHidden] = useLocalStorage<boolean>(
    ELocalStorageKeys.SURVEY_BUILDER_LAYOUT_ALERT_HIDDEN
  );

  const showToast = useToaster();

  const onTryNewLayout = () => {
    setLayoutAlertHidden(true);

    updateStudy({ id: surveyId, config: { enable_unmoderated_topbar_layout: true } });

    showToast(layoutToggleSuccess());

    track('unmoderated_layout_banner_clicked');
  };

  const [blocks, setBlocks] = useState<Models.Block[]>([]);

  useEffect(() => {
    if (initialBlocks.length > 0) {
      setBlocks(initialBlocks);
    }
  }, [initialBlocks]);

  useEffect(() => {
    if (!activeId && initialBlocks[0]?.id) {
      setActiveId(initialBlocks[0].id);
      setActiveBlock?.(initialBlocks[0]);
    }
  }, [initialBlocks, activeId]);

  useEffect(() => {
    if (activeId) {
      setActiveBlock?.(blocks.find(({ id }) => id === activeId));
    }
  }, [activeId]);

  const activeBlock = useMemo(() => blocks?.find(({ id }) => id === activeId), [blocks, activeId]);

  const handleOnCreate = async (kind: Models.Block['kind']) => {
    try {
      const blockable = await createBlockable({ kind }).unwrap();

      const block = await createBlock({
        blockable_id: blockable.id,
        description: null,
        kind,
        required: false,
        surveyId,
        title: null
      }).unwrap();

      setBlocks([...blocks, block]);

      // Set the newly created block as active
      setActiveId(block.id);
      setActiveBlock?.(block);

      return block;
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading && !blocks?.length) {
    return <SurveyBuilderSkeleton />;
  }

  return (
    <>
      {!layoutAlertHidden && (
        <Alert
          className='mb-6'
          type='promo'
          onDismiss={() => setLayoutAlertHidden(true)}
          heading='New layout for participant experience'
        >
          <div className='flex flex-col items-start'>
            <span className='mb-4'>
              We&apos;ve built a new layout for a better participant experience. Give it a try and let us know what you
              think!
            </span>
            <Button medium onClick={onTryNewLayout}>
              Try new layout
            </Button>
          </div>
        </Alert>
      )}
      <div className='h-screen w-full overflow-hidden rounded border border-gray-200 bg-white'>
        {isStudioVersion && (
          <div className='pl-6 pt-6'>
            <CardHeader icon={<AddToInsightSVG />} title='Test' />
          </div>
        )}

        <Grid desktop={12} tablet={12} mobile={12} className={cn('h-full', { 'auto-rows-min': isStudioVersion })}>
          <Column
            id='surveyBuilderBlocks'
            desktop={4}
            tablet={12}
            mobile={12}
            className='min-h-full border-r border-gray-200'
            style={{ contain: isStudioVersion ? 'auto' : 'size' }}
          >
            {blocks && blocks.length > 0 && (
              <Blocks
                active={activeBlock}
                onCreateBlock={handleOnCreate}
                setActive={(block) => {
                  setActiveId(block?.id);
                  setActiveBlock?.(block);
                }}
                surveyId={surveyId}
              />
            )}
          </Column>
          <Column id='surveyBuilderBlockSettings' desktop={8} tablet={12} mobile={12} className='min-h-full'>
            <BlockSettings activeId={activeId} />
          </Column>
        </Grid>

        <Portal>
          {/* Survey builder icons sprite */}
          <SurveyBuilderSpriteSVG style={{ display: 'none' }} />
        </Portal>
      </div>
    </>
  );
};
